import { Popconfirm, Space, Table } from 'antd'
import {
    deleteExistingConnection,
    getAllExistingConnections,
} from '../../controllers/existing_connection_controller'
import RouteConstants from '../../routes_constants'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    DeleteOutlined,
    PlusCircleOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import Loading from '../../molecules/loading'
import Column from 'antd/lib/table/Column'
import Text from 'antd/lib/typography/Text'
import MessageConstants from '../../message_constants'
import FilterDropdown from '../../molecules/filter_dropdown'
import { FilterConfirmProps } from 'antd/lib/table/interface'
import Title from 'antd/lib/typography/Title'
import { useGetUserInfo } from '../../controllers/authentication/clients/auth0'
import { MonitorSingleton } from '../../utils/monitor'
import { validateAndGetUserInfo } from '../../utils/validation_utils'

type DataSourceObject = {
    key: string
    firstName: string
    lastName: string
}

export default function ViewAllConnections() {
    const getUserInfo = useGetUserInfo()
    const [tableData, setTableData] = useState<DataSourceObject[]>([])
    const [doneLoading, setDoneLoading] = useState<boolean>(false)
    const [selectedRows, setSelectedRows] = useState<
        DataSourceObject[] | undefined
    >(undefined)
    const [_searchText, setSearchText] = useState<string | undefined>()
    const [_searchedColumn, setSearchedColumn] = useState<string | undefined>()
    const [confirmDeleting, setConfirmDeleting] = useState(false)
    const navigate = useNavigate()

    const loadUserData = async () => {
        try {
            const userInfo = await validateAndGetUserInfo(getUserInfo, navigate)
            if (!userInfo) {
                return
            }

            const connections = await getAllExistingConnections(userInfo.id)

            setTableData(
                connections.map((connection) => {
                    return {
                        key: connection.entityId,
                        firstName: connection.firstName,
                        lastName: connection.lastName,
                    }
                })
            )
        } catch (err) {
            MonitorSingleton.sendException(err)
            toast(MessageConstants.UNABLE_TO_LOAD_CONNECTIONS, {
                type: 'error',
            })
        } finally {
            setDoneLoading(true)
        }
    }

    useEffect(() => {
        loadUserData()
    }, [])

    let onSelectedRowChange = (
        selectedRowKeys: React.Key[],
        selectedRows: DataSourceObject[]
    ) => {
        if (selectedRows.length === 0) {
            setSelectedRows(undefined)
        } else {
            setSelectedRows(selectedRows)
        }
    }

    let handleSearch = (
        selectedKeys: React.Key[],
        confirm: (param?: FilterConfirmProps | undefined) => void,
        dataIndex: string
    ) => {
        confirm()
        setSearchText(selectedKeys[0]?.toString())
        setSearchedColumn(dataIndex)
    }

    let handleReset = (clearFilters: (() => void) | undefined) => {
        if (clearFilters) {
            clearFilters()
        }
        setSearchText('')
    }

    let filteredIconFunction = (filtered: boolean) => (
        <SearchOutlined
            style={{
                color: filtered ? '#1890ff' : undefined,
            }}
        />
    )

    let handleDeleteClick = async () => {
        setConfirmDeleting(true)
        try {
            const userInfo = await validateAndGetUserInfo(getUserInfo, navigate)

            if (!userInfo || !selectedRows || selectedRows.length <= 0) {
                return
            }
            await Promise.all(
                selectedRows.map((item: DataSourceObject) => {
                    return deleteExistingConnection(userInfo?.id, item.key)
                })
            )
            toast('Connections deleted', {
                type: 'success',
            })
            setSelectedRows(undefined)
            loadUserData()
        } catch (e) {
            toast('Error occurred when trying to delete connection', {
                type: 'error',
            })
        } finally {
            setConfirmDeleting(false)
        }
    }

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Title level={3}>Connections</Title>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    margin: '15px',
                }}
            >
                <Link to={RouteConstants.ADD_CONNECTION}>
                    <PlusCircleOutlined
                        style={{ fontSize: '25px', paddingLeft: '10px' }}
                    />
                </Link>
                {selectedRows && selectedRows.length > 0 && (
                    <Popconfirm
                        title={
                            'Delete selected connection' +
                            (selectedRows.length > 1 ? 's' : '') +
                            '?'
                        }
                        okText={'Delete'}
                        okButtonProps={{ loading: confirmDeleting }}
                        okType="danger"
                        cancelButtonProps={{ disabled: confirmDeleting }}
                        onConfirm={handleDeleteClick}
                    >
                        <DeleteOutlined
                            style={{
                                fontSize: '25px',
                                paddingLeft: '10px',
                                color: 'red',
                            }}
                        />
                    </Popconfirm>
                )}
            </div>
            {
                !doneLoading && <Loading />
                /* TODO: Consider not having a separate loading (data will just naturally be empty?)  --> Maybe use a default (... data type?) */
            }
            {doneLoading && (
                <Table
                    showHeader
                    rowSelection={{
                        preserveSelectedRowKeys: false,
                        type: 'checkbox',
                        onChange: onSelectedRowChange,
                    }}
                    dataSource={tableData}
                >
                    <Column
                        title="First Name"
                        dataIndex="firstName"
                        key="firstName"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.firstName.localeCompare(b.firstName)
                        }
                        width="20%"
                        ellipsis
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="firstName"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['firstName']
                                ? record['firstName']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Last Name"
                        dataIndex="lastName"
                        key="lastName"
                        defaultSortOrder="ascend"
                        sorter={(a: DataSourceObject, b: DataSourceObject) =>
                            a.lastName.localeCompare(b.lastName)
                        }
                        filterDropdown={({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <FilterDropdown
                                    setSelectedKeys={setSelectedKeys}
                                    selectedKeys={selectedKeys}
                                    confirm={confirm}
                                    clearFilters={clearFilters}
                                    dataColumnLabel="lastName"
                                    handleReset={handleReset}
                                    handleSearch={handleSearch}
                                />
                            )
                        }}
                        onFilter={(
                            value: string | number | boolean,
                            record: any
                        ) =>
                            record['lastName']
                                ? record['lastName']
                                      .toString()
                                      .toLowerCase()
                                      .includes(value.toString().toLowerCase())
                                : ''
                        }
                        filterIcon={filteredIconFunction}
                    />
                    <Column
                        title="Action"
                        key="action"
                        render={(text, record: any) => (
                            <Space size="middle">
                                <Text>
                                    <Link
                                        to={RouteConstants.viewExistingConnectionRoute(
                                            record.key
                                        )}
                                    >
                                        View Details
                                    </Link>
                                </Text>
                            </Space>
                        )}
                    />
                </Table>
            )}
        </div>
    )
}
